import { container } from '@/core/container'
import { TYPES } from '../types'

// Use Cases
import {
  LoginUseCase,
  LogoutUseCase,
} from '@/core/application/useCases/AuthenticationUseCases'
import {
  GetDriverListUseCase,
  GetDriverDetailsUseCase,
  GetDriverPerformanceUseCase,
  GetDriverOnlineTimeUseCase,
} from '@/core/application/useCases/DriverUseCases'
import {
  GetRideHistoryUseCase,
  GetDashboardDataUseCase,
} from '@/core/application/useCases/RideUseCases'

import { GetSolicitationMetricsUseCase } from '@/core/application/useCases/SolicitationUseCases'

// Services
import {
  AuthService,
  DriverService,
  RideService,
  SolicitationService,
} from '@/core/application/services'

// Repository interfaces
import { IAuthRepository } from '@/core/domain/repositories/IAuthRepository'
import { IDriverRepository } from '@/core/domain/repositories/IDriverRepository'
import { IRideRepository } from '@/core/domain/repositories/IRideRepository'
import { ISolicitationRepository } from '@/core/domain/repositories/ISolicitationRepository'

export const setupApplicationBindings = () => {
  // Bind Use Cases
  container.registerFactory(TYPES.LoginUseCase, () => {
    const authRepository = container.resolve<IAuthRepository>(
      TYPES.AuthRepository
    )
    return new LoginUseCase(authRepository)
  })

  container.registerFactory(TYPES.LogoutUseCase, () => {
    const authRepository = container.resolve<IAuthRepository>(
      TYPES.AuthRepository
    )
    return new LogoutUseCase(authRepository)
  })

  container.registerFactory(TYPES.GetDriverListUseCase, () => {
    const driverRepository = container.resolve<IDriverRepository>(
      TYPES.DriverRepository
    )
    return new GetDriverListUseCase(driverRepository)
  })

  container.registerFactory(TYPES.GetDriverDetailsUseCase, () => {
    const driverRepository = container.resolve<IDriverRepository>(
      TYPES.DriverRepository
    )
    return new GetDriverDetailsUseCase(driverRepository)
  })

  container.registerFactory(TYPES.GetRideHistoryUseCase, () => {
    const rideRepository = container.resolve<IRideRepository>(
      TYPES.RideRepository
    )
    return new GetRideHistoryUseCase(rideRepository)
  })

  container.registerFactory(TYPES.GetDashboardDataUseCase, () => {
    const rideRepository = container.resolve<IRideRepository>(
      TYPES.RideRepository
    )
    const driverRepository = container.resolve<IDriverRepository>(
      TYPES.DriverRepository
    )

    return new GetDashboardDataUseCase(rideRepository, driverRepository)
  })

  container.registerFactory(TYPES.GetSolicitationMetricsUseCase, () => {
    const solicitationRepository = container.resolve<ISolicitationRepository>(
      TYPES.SolicitationRepository
    )
    return new GetSolicitationMetricsUseCase(solicitationRepository)
  })

  // Bind Application Services
  container.registerFactory(TYPES.AuthService, () => {
    const loginUseCase = container.resolve<LoginUseCase>(TYPES.LoginUseCase)
    const logoutUseCase = container.resolve<LogoutUseCase>(TYPES.LogoutUseCase)
    return new AuthService(loginUseCase, logoutUseCase)
  })

  container.registerFactory(TYPES.GetDriverPerformanceUseCase, () => {
    const driverRepository = container.resolve<IDriverRepository>(
      TYPES.DriverRepository
    )
    return new GetDriverPerformanceUseCase(driverRepository)
  })

  container.registerFactory(TYPES.GetDriverOnlineTimeUseCase, () => {
    const driverRepository = container.resolve<IDriverRepository>(
      TYPES.DriverRepository
    )
    return new GetDriverOnlineTimeUseCase(driverRepository)
  })

  container.registerFactory(TYPES.DriverService, () => {
    const getDriverListUseCase = container.resolve<GetDriverListUseCase>(
      TYPES.GetDriverListUseCase
    )
    const getDriverDetailsUseCase = container.resolve<GetDriverDetailsUseCase>(
      TYPES.GetDriverDetailsUseCase
    )
    const getDriverPerformanceUseCase =
      container.resolve<GetDriverPerformanceUseCase>(
        TYPES.GetDriverPerformanceUseCase
      )
    const getDriverOnlineTimeUseCase =
      container.resolve<GetDriverOnlineTimeUseCase>(
        TYPES.GetDriverOnlineTimeUseCase
      )
    return new DriverService(
      getDriverListUseCase,
      getDriverDetailsUseCase,
      getDriverPerformanceUseCase,
      getDriverOnlineTimeUseCase
    )
  })

  container.registerFactory(TYPES.RideService, () => {
    const getRideHistoryUseCase = container.resolve<GetRideHistoryUseCase>(
      TYPES.GetRideHistoryUseCase
    )

    const getDashboardDataUseCase = container.resolve<GetDashboardDataUseCase>(
      TYPES.GetDashboardDataUseCase
    )

    return new RideService(getRideHistoryUseCase, getDashboardDataUseCase)
  })

  container.registerFactory(TYPES.SolicitationService, () => {
    const getSolicitationMetricsUseCase =
      container.resolve<GetSolicitationMetricsUseCase>(
        TYPES.GetSolicitationMetricsUseCase
      )

    return new SolicitationService(getSolicitationMetricsUseCase)
  })
}
